import React, { useState } from 'react'
import { useToast } from '@chakra-ui/react'
import ConfirmDialog from './ConfirmDialog'
import InviteUserDialog from './InviteUserDialog'
import RolePermissionsDialog from './RolePermissionsDialog'
import RolePicker from './RolePicker'
import Style from './users.module.css'
import { CLIENT_PERMISSION, splitClientRoles } from '../../../features/clientUsers/clientAccess'
import { clientGrants } from '../../../features/clientUsers/useClientAccess'
import {
    useChangeClientUserStatusMutation,
    useGetClientUsersQuery,
    useGetRoleCatalogQuery,
    useResetClientUserPasswordMutation,
    useUpdateClientUserRoleMutation
} from '../../../features/clientUsers/clientUsersApi'

const EMPTY = []

const roleLabel = (role, roles) => {
    const labels = splitClientRoles(role).map((value) => {
        const match = roles.find((item) => item.value === value)
        return match ? match.label : value
    })
    return labels.length > 0 ? labels.join(', ') : 'Not assigned'
}

const ClientUsers = ({ client }) => {
    const toast = useToast()
    const usersQuery = useGetClientUsersQuery(client.id)
    const { data: catalog, isError: isCatalogError } = useGetRoleCatalogQuery()
    const [changeStatus] = useChangeClientUserStatusMutation()
    const [resetPassword] = useResetClientUserPasswordMutation()
    const [updateRole] = useUpdateClientUserRoleMutation()
    const [isInviting, setIsInviting] = useState(false)
    const [isShowingPermissions, setIsShowingPermissions] = useState(false)
    const [confirmation, setConfirmation] = useState(null)
    const [isConfirming, setIsConfirming] = useState(false)

    const roles = (catalog && catalog.roles) || EMPTY
    const users = usersQuery.data || EMPTY
    const canInvite = clientGrants(client, CLIENT_PERMISSION.USER_INVITE)
    const canChangeStatus = clientGrants(client, CLIENT_PERMISSION.USER_CHANGE_STATUS)
    const canAssignRole = clientGrants(client, CLIENT_PERMISSION.USER_ASSIGN_ROLE)
    const canResetPassword = clientGrants(client, CLIENT_PERMISSION.USER_RESET_PASSWORD)

    const notify = (status, title) => toast({ title, status, duration: 4000, isClosable: true, position: 'top-right' })

    // The API answers 200 with `result: false` and a message when it refuses an action.
    const runAction = (request, successMessage, failureMessage) => request
        .unwrap()
        .then((response) => {
            if (response && response.result) {
                notify('success', successMessage)
                return true
            }
            notify('error', (response && response.message) || failureMessage)
            return false
        })
        .catch(() => {
            notify('error', failureMessage)
            return false
        })

    const saveRoles = (user, role) => runAction(
        updateRole({ id: user.id, clientId: client.id, role }),
        'Role updated',
        'Failed to update role'
    )

    const confirmStatusChange = (user, isActive) => {
        const action = isActive ? 'Activate' : 'Deactivate'
        setConfirmation({
            title: `${action} user`,
            message: `Are you sure you want to ${action.toLowerCase()} "${user.userName}"?`,
            confirmLabel: action,
            isDanger: !isActive,
            run: () => runAction(
                changeStatus({ id: user.id, clientId: client.id, isActive }),
                `User ${isActive ? 'activated' : 'deactivated'}`,
                'Failed to update user status'
            )
        })
    }

    const confirmPasswordReset = (user) => setConfirmation({
        title: 'Reset password',
        message: `Send "${user.userName}" a new temporary password by email? Their current password will stop working.`,
        confirmLabel: 'Reset password',
        isDanger: true,
        run: () => runAction(
            resetPassword({ id: user.id }),
            `A temporary password was emailed to ${user.emailAddress}`,
            'Failed to reset password'
        )
    })

    const runConfirmation = () => {
        setIsConfirming(true)
        confirmation.run().finally(() => {
            setIsConfirming(false)
            setConfirmation(null)
        })
    }

    const renderBody = () => {
        if (usersQuery.isLoading) {
            return <p className={Style.state} role="status">Loading users...</p>
        }
        if (usersQuery.isError) {
            return (
                <div className={Style.state} role="alert">
                    <p>The users could not be loaded.</p>
                    <button type="button" className={Style.ghostButton} onClick={usersQuery.refetch}>Try again</button>
                </div>
            )
        }
        if (users.length === 0) {
            return <p className={Style.state}>No users yet. Invited users will appear here.</p>
        }
        return (
            <div className={Style.tableScroll}>
                <table className={Style.table}>
                    <thead>
                        <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Roles</th>
                            <th scope="col">Status</th>
                            <th scope="col" className={Style.actionsCell}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td className={Style.nameCell}>{user.userName}</td>
                                <td>{user.emailAddress}</td>
                                <td className={Style.rolesCell}>
                                    {canAssignRole && roles.length > 0 ? (
                                        <RolePicker
                                            user={user}
                                            roles={roles}
                                            onSave={(role) => saveRoles(user, role)}
                                            onEmpty={() => notify('error', 'Select at least one role')}
                                        />
                                    ) : roleLabel(user.role, roles)}
                                </td>
                                <td>
                                    <div className={Style.statusCell}>
                                        <span className={[Style.badge, user.isActive ? Style.badgeActive : ''].join(' ')}>
                                            {user.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        {canChangeStatus ? (
                                            <label className={`switch ${Style.switchWrap}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={user.isActive}
                                                    aria-label={`Toggle status for ${user.userName}`}
                                                    onChange={(event) => confirmStatusChange(user, event.target.checked)}
                                                />
                                                <span className="slider round" />
                                            </label>
                                        ) : null}
                                    </div>
                                </td>
                                <td className={Style.actionsCell}>
                                    {canResetPassword && user.isActive ? (
                                        <button
                                            type="button"
                                            className={Style.ghostButton}
                                            aria-label={`Reset password for ${user.userName}`}
                                            onClick={() => confirmPasswordReset(user)}
                                        >
                                            <i className="uil-key-skeleton" aria-hidden="true" /> Reset password
                                        </button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <section className={Style.card}>
            <header className={Style.cardHeader}>
                <div className={Style.cardTitle}>
                    <h2 className={Style.cardTitleText}>{client.name}</h2>
                    {usersQuery.data ? <span className={Style.count}>{users.length}</span> : null}
                </div>
                <div className={Style.toolbar}>
                    <button type="button" className={Style.ghostButton} onClick={() => setIsShowingPermissions(true)}>
                        <i className="uil-shield-check" aria-hidden="true" /> Role permissions
                    </button>
                    {canInvite ? (
                        <button type="button" className={Style.primaryButton} onClick={() => setIsInviting(true)}>
                            <i className="uil-user-plus" aria-hidden="true" /> Invite user
                        </button>
                    ) : null}
                </div>
            </header>

            {isCatalogError ? <p className={Style.warning} role="alert">The roles could not be loaded, so roles can't be changed right now.</p> : null}

            {renderBody()}

            {isInviting ? (
                <InviteUserDialog
                    clientId={client.id}
                    roles={roles}
                    canAssignRole={canAssignRole}
                    onClose={() => setIsInviting(false)}
                />
            ) : null}
            {isShowingPermissions ? (
                <RolePermissionsDialog
                    catalog={catalog}
                    isError={isCatalogError}
                    onClose={() => setIsShowingPermissions(false)}
                />
            ) : null}
            {confirmation ? (
                <ConfirmDialog
                    title={confirmation.title}
                    message={confirmation.message}
                    confirmLabel={confirmation.confirmLabel}
                    isDanger={confirmation.isDanger}
                    isBusy={isConfirming}
                    onConfirm={runConfirmation}
                    onCancel={() => setConfirmation(null)}
                />
            ) : null}
        </section>
    )
}

export default ClientUsers
